<script lang="ts">
	import type {
		PartScreenStageOne$result,
		LIB_partStoreData$result,
		NewInventory,
		InventoryUpdate,
		InventoryLocationUpdates,
		NestedInventoryAttachmentUpdates,
		InventoryOptionValueUpdate,
		NestedInventorySerialUpdate,
		ActionEnum$options,
		InventoryReplicationResult$options as InventoryReplicationResult,
		LIB_replicateInventory$result,
		NewCollectionMember,
		CollectionMemberUpdate,
	} from '$houdini'
	import type {
		CollectionMember,
		FlatAttachment,
		LoadedCollectionMember,
		OptionValueMap,
		PartForClient as Part,
		SettingValues,
		TypeField,
		UserSettings,
	} from 'utility/load-part'
	import type { PartModel } from 'utility/load-models-for-manufacturer'
	import type { Mediator, SvelteAsr } from 'types/common'
	import type { Writable } from 'svelte/store'
	import type { GetFieldEditabilityFn } from './part'

	import Button from '@isoftdata/svelte-button'
	import Modal from '@isoftdata/svelte-modal'
	import { NavTabBar } from '@isoftdata/svelte-nav-tab-bar'
	import Attachments from '@isoftdata/svelte-attachments'
	import Dropdown, { DropdownItem, DropdownCheckboxItem } from '@isoftdata/svelte-dropdown'
	import TextArea from '@isoftdata/svelte-textarea'
	import PartBasic from './Basic.svelte'
	import Links from './Links.svelte'
	import History from './History.svelte'
	import StoreSelectModal from 'components/StoreSelectModal.svelte'
	import AutoRefreshThing from 'components/AutoRefreshThing.svelte'
	import PartSearchModal from 'components/PartSearchModal.svelte'
	import ReportJobModal from 'components/ReportJobModal.svelte'

	import session from 'stores/session'
	import debounced from 'utility/debounce'
	import { klona } from 'klona'
	import { dequal } from 'dequal'
	import { graphql } from '$houdini'
	import { fade } from 'svelte/transition'
	import { v4 as uuid } from '@lukeed/uuid'
	import { hasPermission } from 'utility/permission'
	import { CrudRuneStore } from '@isoftdata/svelte-store-crud'
	import { readAsDataURL } from '@isoftdata/promise-file-reader'
	import { getObject, setObject } from '@isoftdata/utility-storage'
	import { PersistedUserState } from '@isoftdata/svelte-store-user-local-writable'
	import { getContext, onMount, setContext, tick, type ComponentProps } from 'svelte'

	const lastInventorySearch = new PersistedUserState<Record<string, unknown>>(
		$session.userAccountId,
		'lastInventorySearch',
		{},
	)

	interface Props {
		asr: SvelteAsr
		settingValues: SettingValues
		userSettings: UserSettings
		clearLocationsOnCopy: boolean
		partStoreLocations: LIB_partStoreData$result['locations']
		part: Part
		attachments: Array<FlatAttachment>
		typeFields: Array<TypeField>
		origPart: Readonly<Part>
		optionValueMap: OptionValueMap
		loadCachedPart: boolean
		sellPriceClasses: PartScreenStageOne$result['sellPriceClasses']
		trueModels: Array<PartModel>
		assyModels: Array<PartModel>
		storeId: number
		tab?: 'basic' | 'attachments' | 'history' | 'links'
		glCategories: PartScreenStageOne$result['glCategories']
		inventoryConditions: PartScreenStageOne$result['inventoryConditions']
		inventoryTypeList: PartScreenStageOne$result['inventoryTypeList']
		saleClasses: PartScreenStageOne$result['saleClasses']
		userPartStatusList: PartScreenStageOne$result['userPartStatusList']
		vehicleMakes: PartScreenStageOne$result['vehicleMakes']
		vehicleModels?: Array<string>
		vendorList: PartScreenStageOne$result['vendors']['items']
		clearScreenOnSave: Writable<boolean>
		userCanEdit: GetFieldEditabilityFn
	}

	let {
		asr,
		settingValues,
		userSettings,
		clearLocationsOnCopy,
		partStoreLocations,
		part = $bindable(),
		attachments = $bindable(),
		typeFields = $bindable(),
		origPart = $bindable(),
		optionValueMap = $bindable(),
		loadCachedPart,
		sellPriceClasses,
		trueModels,
		assyModels,
		storeId,
		tab = $bindable('basic'),
		glCategories,
		inventoryConditions,
		inventoryTypeList,
		saleClasses,
		userPartStatusList,
		vehicleMakes,
		vehicleModels = [],
		vendorList,
		clearScreenOnSave,
		userCanEdit,
	}: Props = $props()

	const mediator = getContext<Mediator>('mediator')
	const tabs: ComponentProps<typeof NavTabBar>['tabs'] = [
		{
			name: 'basic',
			title: 'Basic Info',
		},
		{
			name: 'attachments',
			title: 'Attachments',
		},
		{
			name: 'history',
			title: 'History',
		},
		{
			name: 'links',
			title: 'Links',
		},
	] as const
	const collectionCrudStore = new CrudRuneStore<CollectionMember, 'uuid'>('uuid')
	setContext('collectionCrudStore', collectionCrudStore)

	let partChanged: boolean = $state(false)
	let showNoLocationOnSaveModal = $state(false)
	let loadPartModal: PartSearchModal = $state()!
	let autoRefreshThing: AutoRefreshThing = $state()!
	let storeSelectModal: StoreSelectModal = $state()!
	let skipChangeDetection = true
	let scrollY = $state(0)
	let showStoreModal = $state(false)
	/** whether the part history modal is in "saving mode", ie we are adding a changelog entry before saving*/
	let showPartChangeModal = $state(false)
	let partChangesToLog: Array<{
		property: string
		previousValue: string | null
		newValue: string | null
	}> = $state([])
	let partChangeComment = $state('')
	let reportJobModal: ReportJobModal = $state()!

	let partTitle = $derived(
		part
			? {
					title: part.tagNumber || part.innodbInventoryid || 'New Part',
					subtitle: userSettings.parts.showSkuField
						? `(${part.inventoryTypeId}) SKU: ${part.skuNumber || 'N/A'}`
						: `(${part.inventoryTypeId || 'N/A'})`,
			  }
			: { title: 'New Part', subtitle: '' },
	)
	let inventoryTypeData = $derived(inventoryTypeList.find(it => it.inventoryTypeId === part.inventoryTypeId) ?? null)
	let missingRequiredFields = $derived(getMissingRequiredFields(part))
	let missingRequiredFieldsList = $derived(missingRequiredFields.join(', ') || '')
	let requiredFieldsComplete = $derived(missingRequiredFields.length === 0)
	let isSaving = $derived.by(() => $updateInventory.fetching || $createInventory.fetching)
	let partStoreIds = $derived(part.skuType.inventories.map(inv => inv.store.id))

	$effect(() => {
		JSON.stringify(part) // without this, the effect wont be trigger as the part itself doesnt change
		debouncedCachePart(part)
	})

	// This is just the inverse of `canEdit` so we don't have to flip it when called
	const shouldDisable = (fieldName: Parameters<typeof userCanEdit>[0]) => {
		return !userCanEdit(fieldName)
	}

	function getMissingRequiredFields(part: Part): Array<string> {
		const isNew = !part.innodbInventoryid
		const missingFields: Array<string> = []

		if (!settingValues?.inventory) {
			return missingFields
		}

		const checks: Array<{
			name: string
			missingRequiredField: boolean | null | undefined
		}> = [
			{
				name: 'Category',
				missingRequiredField: settingValues.inventory.fields.category.required && !part.category?.id,
			},
			{
				name: 'Condition',
				missingRequiredField: settingValues.inventory.fields.condition.required && !part.condition,
			},
			{
				name: 'Cost',
				missingRequiredField: settingValues.inventory.fields.cost.required && !part.cost,
			},
			{
				name: 'GL Category',
				missingRequiredField: settingValues.inventory.fields.glCategory.required && !part.glCategory?.id,
			},
			{
				name: 'Inventory Type',
				missingRequiredField: !part.inventoryTypeId,
			},
			{
				name: 'Jobber Price',
				missingRequiredField: settingValues.inventory.fields.jobberPrice.required && !part.jobberPrice,
			},
			{
				name: 'OEM Number',
				missingRequiredField:
					settingValues.inventory.fields.oemNumber.required &&
					(isNew || part.oemNumber !== origPart.oemNumber) &&
					!part.oemNumber,
			},
			{
				name: 'Retail Price',
				missingRequiredField: settingValues.inventory.fields.retailPrice.required && !part.retailPrice,
			},
			{
				name: 'Retail Core Price',
				missingRequiredField: settingValues.inventory.fields.retailCorePrice.required && !part.retailCorePrice,
			},
			{
				name: 'Side',
				missingRequiredField: settingValues.inventory.fields.side.required && !part.side,
			},
			{
				name: 'User Status',
				missingRequiredField: settingValues.inventory.fields.userStatus.required && !part.userStatus,
			},
			{
				name: 'Wholesale Price',
				missingRequiredField: settingValues.inventory.fields.wholesalePrice.required && !part.wholesalePrice,
			},
			{
				name: 'Serial Number',
				missingRequiredField:
					inventoryTypeData?.requireSerialization &&
					(!part.serialized || !part.serials.length || !part.serials[0]?.number),
			},
			{
				name: 'Collection Type',
				missingRequiredField: !!part.collection && !part.collection.type,
			},
			{
				name: 'Collection Members',
				missingRequiredField: !!part.collection && !part.collection.members.length,
			},
			// TODO fields on collection members
		]

		for (const check of checks) {
			if (check.missingRequiredField) {
				missingFields.push(check.name)
			}
		}
		return missingFields
	}

	function clonePart(): void {
		if (!hasPermission('PARTS_CAN_CLONE_PARTS', part.storeId)) {
			return alert('You do not have permission to clone parts.')
		}

		if (partChanged) {
			return alert('You must save your changes before cloning a part.')
		}

		// Klona part
		const clonedPart: Part = {
			...klona(part),
			// NULL inventoryId/innoDBInventoryId
			inventoryId: null,
			skuNumber: null,
			innodbInventoryid: null,
			// Set tag # to blank
			tagNumber: '',
			// tagPrinted = false
			tagPrinted: false,
			// Zero-out hold quantity
			// zero out current quantity
			quantity: 0,
			quantityAvailable: 0,
			quantityOnHold: 0,
			// zero average (core) cost
			averageCoreCost: '0',
			averageCost: '0',
			// Forget all loaded serials and associated Q&A
			serials: [],
			// re-perform the vehicle load operation.  It reloads the vehicle information onto the screen in case the part we’re copying changed it or had it out of date.  This isn't strictly 'copying', but it ensures we have the vehicle location
			vehicleMake: part.vehicle?.manufacturer?.name ?? null,
			vehicleModel: part.vehicle?.model?.name ?? null,
			vehicleYear: part.vehicle?.year ?? null,
			vehicleVin: part.vehicle?.vin ?? null,
			// zero out quantity/hold quantity at all locations, mark as "new", forget previous inventoryLocation.inventoryId
			locations: clearLocationsOnCopy
				? []
				: part.locations
						.filter(location => !location.deleted)
						.map(location => ({
							...klona(location),
							// inventoryLocationId -> null
							id: null,
							uuid: uuid(),
							holdQuantity: 0,
							quantity: 0,
						})),
		}
		// For the ‘default’ serial (global/default answers), mark them as new. Also, use this opportunity to go through any and make sure they are valid for the part and remove the ones that aren’t (since we don’t proactively delete answers that are on questions that don’t apply).  Put in blank values for any options that didn’t have an answer (so that anything they didn’t fill out previously don’t suddenly have a default value pop in)
		// I think we can just delete the "serial" answers and call it a day?
		const defaultQaEntries = optionValueMap.entries().filter(([key]) => !key.serialId && !key.serialUuid)

		// "forget" deleted attachments, bring over attachments but "unassociated" (keep fileId, drop inventoryFile records)
		const clonedAttachments = klona(attachments)
			.filter(attachment => attachment.action !== 'DELETE')
			.map(attachment => ({
				...attachment,
				inventoryFileId: null,
				action: 'CREATE' as const,
			}))

		// These TODOs should happen eventually, but the API doesn't support them yet
		// TODO: For all loaded inventorylookups on the lookups screen, any historically deleted ones are cleaned out, any others are marked as new, and forget their previous inventorylookup.inventoryid for next save
		// TODO: This part’s collection (if any) is marked as new
		// TODO: For all messages on the messages screen, any historically deleted ones are cleared.  Any others are marked as new
		// TODO: Clear out the transaction list history (?)

		// cache everything and load the cached part
		clearCachedPart()
		cachePart(clonedPart)
		setObject(localStorage, 'cachedOptionValues', {
			keyParts: optionValueMap.keyParts,
			entries: defaultQaEntries,
		})
		cacheAttachments(clonedAttachments)
		alert(
			"A clone has been created with minor modifications (initial quantity of 0, no serial #'s).\nSimply save the part when you are done making changes.",
		)
		// go to cached part
		partChanged = false
		asr.go(null, { loadCachedPart: true, storeId: clonedPart.storeId, sku: null }, { replace: true })
	}

	function replicatePart(): void {
		if (partChanged) {
			return alert('You must save your changes before replicating this part.')
		}

		if (!storeSelectModal.hasValidStores()) {
			return alert('All stores you have access to already have a version of this part. Replication impossible.')
		}

		storeSelectModal.open()
	}

	async function confirmReplicatePart(destinationStoreIds: Array<number>): Promise<void> {
		if (!part.innodbInventoryid || partChanged) {
			return alert('You must save this part before replicating it.')
		}

		const { data } = await replicateInventory.mutate({
			sourceInventoryId: part.innodbInventoryid,
			destinationStoreIds,
		})

		const results = data?.replicateInventory.reduce(
			(acc: Record<InventoryReplicationResult, Array<number>>, res) => {
				acc[res.result].push(res.storeId)
				return acc
			},
			{
				DUPLICATE: [],
				FAILURE: [],
				SUCCESS: [],
			},
		) ?? {
			DUPLICATE: [],
			FAILURE: [],
			SUCCESS: [],
		}

		let messageParts: Array<string> = []

		type ResultWithPart = LIB_replicateInventory$result['replicateInventory'][number] & {
			inventory: NonNullable<LIB_replicateInventory$result['replicateInventory'][number]['inventory']>
		}

		if (results.SUCCESS.length) {
			messageParts.push(`Successfully replicated to stores: ${results.SUCCESS.join(', ')}`)
			const newStores =
				data?.replicateInventory
					.filter((res): res is ResultWithPart => res.result === 'SUCCESS' && !!res.inventory)
					.map(res => res.inventory) ?? []
			part.skuType.inventories.push(...newStores)
		}

		if (results.FAILURE.length) {
			messageParts.push(`Failed to replicate to stores: ${results.FAILURE.join(', ')}`)
		}

		if (results.DUPLICATE.length) {
			messageParts.push(`Already replicated to stores: ${results.DUPLICATE.join(', ')}`)
		}

		alert(messageParts.join('\n\n'))
	}

	// #region Caching
	function clearCachedPart(): void {
		localStorage.removeItem('cachedPart')
		localStorage.removeItem('cachedOptionValues')
		localStorage.removeItem('cachedPartAttachments')
		void mediator.publish('removeActivity', 'Unsaved Part')
	}

	function createUnsavedPartActivity(): void {
		mediator.publish('activity', {
			title: 'Unsaved Part',
			route: 'app.part',
			parameters: {
				sku: part.skuNumber ? part.skuNumber.toString() : 'null',
				storeId: storeId.toString(),
				loadCachedPart: 'True',
			},
			type: 'PART',
			name: 'Part',
		})
		partChanged = true
	}

	const debouncedCachePart = debounced(500, (thePart: Part) => cachePart(thePart)) as (part: Part) => void
	// observe part.*
	async function cachePart(part: Part): Promise<void> {
		// Rendering a component that binds to the part will trigger this observer, so ignore it if the actual value hasn't changed
		try {
			if (skipChangeDetection || (!partChanged && dequal(part, origPart))) {
				return
			}
			if (!partChanged) {
				// Remove old "Unsaved Part"s before we add the new one since we only store one unsaved part in memory
				clearCachedPart()
				createUnsavedPartActivity()
			}
			const partToCache = klona(part)

			setObject(localStorage, 'cachedPart', partToCache)
		} catch (err) {
			// This doesn't matter so just ignore it
		}
	}

	function cacheOptionValueMap(): void {
		if (skipChangeDetection) {
			return
		}
		if (!partChanged) {
			clearCachedPart()
			createUnsavedPartActivity()
			cachePart(part)
		}
		// Unfortunately if we want to cache the part on every change, we have to loop through all option values every time one is changed
		setObject(localStorage, 'cachedOptionValues', {
			keyParts: optionValueMap.keyParts,
			entries: optionValueMap.entries(),
		})
	}

	async function cacheAttachments(attachmentsInput: typeof attachments = attachments): Promise<void> {
		if (skipChangeDetection || !attachmentsInput.length) {
			return
		}
		if (!partChanged) {
			clearCachedPart()
			createUnsavedPartActivity()
			// need to also cache the part if we want to reload it later
			cachePart(part)
		}
		const attachmentsToCache = await Promise.all(
			klona(attachmentsInput).map(async attachment => {
				if (attachment.path?.startsWith('blob:') && attachment.File) {
					return {
						...attachment,
						path: await readAsDataURL(attachment.File), // so we can restore the image on reload
					}
				}
				return attachment
			}),
		)
		setObject(localStorage, 'cachedPartAttachments', attachmentsToCache)
	}
	//#endregion Caching
	// #region Saving
	function getOptionValuesForSave(
		thePart = part,
		theOptionValueMap = optionValueMap,
	): Record<string | number, Array<InventoryOptionValueUpdate>> {
		const optionIdSet = new Set(thePart.inventoryOptions.map(({ id }) => id))

		const optionValuesToSave = Array.from(theOptionValueMap.entries()).reduce(
			(
				acc: Record<string | number, Array<InventoryOptionValueUpdate>> & {
					optionValues: Array<InventoryOptionValueUpdate>
				},
				[{ serialId, optionId, serialUuid }, value],
			) => {
				if (optionId && !optionIdSet.has(optionId)) {
					return acc
				}
				// serialUuid is used for new serials
				const serialKey = serialId || serialUuid
				// Have to include ALL optionValues for a given serial or else the absent ones will be cleared
				// Could be made more efficient if we exclude serials with no changes made to them. But if any are modified, we need to send the whole list.
				if (serialKey && optionId) {
					// Serial Q&A
					if (!acc[serialKey]) {
						acc[serialKey] = []
					}
					acc[serialKey].push({
						inventoryOptionId: optionId,
						value: value?.toString?.() ?? '',
					})
				} else if (optionId) {
					// Default / non-serial Q&A
					acc.optionValues.push({
						inventoryOptionId: optionId,
						value: value?.toString?.() ?? '',
					})
				}
				return acc
			},
			{ optionValues: [] },
		)

		return optionValuesToSave
	}

	function addLocationAndSave(locationName: string): void {
		const existingLocation = partStoreLocations.find(
			location => location.name.toLowerCase() === locationName.toLowerCase(),
		)
		part.locations.push({
			description: existingLocation?.description ?? '',
			allowInventory: true,
			id: null,
			locationId: existingLocation?.id ?? null,
			name: locationName,
			rank: 1,
			quantity: part.quantity,
			holdQuantity: 0,
			permanent: false,
			uuid: uuid(),
			deleted: false,
		})
		part.locations = part.locations
		showNoLocationOnSaveModal = false
		savePart()
	}

	type DeepNonNullRequired<T> = Required<T> & {
		[K in keyof Required<T>]: Exclude<Required<T>[K], null>
	}

	type GenericPartForSave = Omit<NewInventory, 'collection' | 'inventoryTypeId' | 'storeId'> &
		Omit<InventoryUpdate, 'id'>
	type AttachmentsForSave = DeepNonNullRequired<NestedInventoryAttachmentUpdates>
	type LocationsForSave = DeepNonNullRequired<InventoryLocationUpdates>
	type SerialForSave = NestedInventorySerialUpdate & { number: string }

	type PartInfoForSave = {
		part: GenericPartForSave
		attachments: AttachmentsForSave
		serials: Array<SerialForSave>
		optionValues: Array<InventoryOptionValueUpdate>
		locations: LocationsForSave
	}

	function getGenericPartForSave(
		thePart = part,
		theAttachments = attachments,
		theOptionValueMap = optionValueMap,
	): PartInfoForSave {
		const { optionValues, ...serialOptionValues } = getOptionValuesForSave(thePart, theOptionValueMap)

		return {
			attachments: theAttachments.reduce(
				({ create, remove, update }: AttachmentsForSave, attachment) => {
					if (attachment.action === 'CREATE' && attachment.File) {
						create.push({
							file: attachment.File,
							public: attachment.public,
							rank: attachment.rank,
						})
					} else if (attachment.action === 'CREATE' && attachment.fileId) {
						create.push({
							fileId: attachment.fileId,
							public: attachment.public,
							rank: attachment.rank,
						})
					} else if (attachment.action === 'UPDATE' && attachment.inventoryFileId) {
						update.push({
							id: attachment.inventoryFileId,
							public: attachment.public,
							rank: attachment.rank,
						})
					} else if (attachment.action === 'DELETE' && attachment.inventoryFileId) {
						remove.push(attachment.inventoryFileId)
					}

					return { create, remove, update }
				},
				{ create: [], remove: [], update: [] },
			),
			locations: thePart.locations.reduce(
				(
					{ create, update }: LocationsForSave,
					{ id: inventoryLocationId, locationId, name: locationName, rank, quantity, permanent, deleted },
				) => {
					if (!inventoryLocationId) {
						create.push({
							locationId: Number.isInteger(locationId) ? locationId : null,
							locationName,
							permanent,
							quantity: quantity.toString(),
							rank,
						})
					} else {
						update.push({
							id: inventoryLocationId,
							permanent: deleted ? false : permanent,
							quantity: deleted ? '0' : quantity.toString(),
							rank,
						})
					}
					// quantity 0, permanent false -> remove
					return { create, update }
				},
				{ create: [], update: [] },
			),
			optionValues,
			part: {
				bodyStyle: !thePart.vehicleId || thePart.innodbInventoryid ? thePart.bodyStyle : undefined, // Standard inventory will inherit from vehicle
				buyPackage: thePart.replenishable ? thePart.buyPackage : undefined, // replenishable only
				// don't update category if it's -1 (inactive category)
				categoryId: thePart.category?.id === -1 ? undefined : thePart.category?.id ?? null,
				condition: thePart.condition,
				coreClass: thePart.replenishable ? thePart.coreClass : undefined,
				coreCost: thePart.coreCost,
				coreRequired: thePart.coreRequired,
				coreRequiredToVendor: thePart.coreRequiredToVendor,
				cost: thePart.cost,
				daysToReturn: thePart.daysToReturn,
				daysToReturnCoreToVendor: thePart.daysToReturnCoreToVendor,
				daysToReturnToVendor: thePart.daysToReturnToVendor,
				daysToReturnCore: thePart.daysToReturnCore,
				deplete: thePart.deplete,
				description: thePart.description,
				distributorCorePrice: thePart.distributorCorePrice,
				distributorPrice: thePart.distributorPrice,
				freezeUntil: thePart.replenishable ? thePart.freezeUntil : undefined, // replenishable only
				glCategoryId: thePart.glCategory?.id || null,
				interchangeNumber: thePart.interchangeNumber,
				jobberCorePrice: thePart.jobberCorePrice,
				jobberPrice: thePart.jobberPrice,
				listPrice: thePart.listPrice,
				manufacturerId: thePart.manufacturer?.id || null,
				maxQuantity: thePart.replenishable ? thePart.maxQuantity.toString() : undefined, // replenishable only
				minQuantity: thePart.replenishable ? thePart.minQuantity.toString() : undefined, // replenishable only
				modelId: thePart.model?.id ?? null,
				notes: thePart.notes,
				oemNumber: thePart.oemNumber,
				optionValues,
				parentManufacturerId: thePart.parentManufacturer?.id ?? null,
				parentModelId: thePart.parentModel?.id || null,
				partNumber: thePart.replenishable ? thePart.partNumber : undefined,
				popularityCode: thePart.popularityCode,
				printTag: !thePart.tagPrinted,
				public: thePart.public,
				purchaseFactor: thePart.replenishable ? thePart.useVendorOrderMultiplier : undefined,
				quantity: hasPermission('PARTS_CAN_EDIT_QUANTITY', thePart.storeId) ? thePart.quantity.toString() : undefined,
				replenishable: thePart.innodbInventoryid ? thePart.replenishable : undefined, // only for update
				retailCorePrice: thePart.retailCorePrice,
				retailPrice: thePart.retailPrice,
				returnable: thePart.returnable,
				returnableToVendor: thePart.returnableToVendor,
				safetyStockPercent: thePart.safetyStockPercent.toString(),
				saleClass: {
					code: thePart.saleClass?.code || null,
				},
				seasonal: thePart.seasonal,
				sellPackage: thePart.sellPackage,
				sellPriceClassId: thePart.sellPriceClassId,
				serialized: thePart.serialized || origPart.serialized, // do not allow un-serializing a thePart
				shippingDimensions: thePart.shippingDimensions,
				side: thePart.side || 'NA', // have to pass a side option to the API
				singleQuantity: thePart.singleQuantity,
				status: thePart.status || 'A',
				stockingDays: thePart.stockingDays,
				stockMethod: thePart.replenishable ? thePart.stockMethod : undefined,
				stockType: thePart.replenishable ? thePart.stockType : undefined,
				subInterchangeNumber: thePart.subInterchangeNumber,
				tagNumber: thePart.tagNumber || undefined,
				taxable: thePart.taxable,
				typeData1: thePart.typeField1?.data,
				typeData2: thePart.typeField2?.data,
				typeData3: thePart.typeField3?.data,
				typeData4: thePart.typeField4?.data,
				userStatus: thePart.userStatus || null,
				vehicleId: thePart.vehicleId || undefined, // only for Standard Inventory & Updates
				vehicleYear: !thePart.vehicleId || thePart.innodbInventoryid ? thePart.vehicleYear || null : undefined, // Standard inventory will inherit from vehicle
				vehicleModel: !thePart.vehicleId || thePart.innodbInventoryid ? thePart.vehicleModel : undefined, // Standard inventory will inherit from vehicle
				vehicleMake: !thePart.vehicleId || thePart.innodbInventoryid ? thePart.vehicleMake : undefined, // Standard inventory will inherit from vehicle
				vehicleVin: !thePart.vehicleId || thePart.innodbInventoryid ? thePart.vehicleVin : undefined, // Standard inventory will inherit from vehicle
				vendorPopularityCode: thePart.replenishable ? thePart.vendorPopularityCode : undefined, // only replenishable
				vendorProductCode: thePart.replenishable || thePart.innodbInventoryid ? thePart.vendorProductCode : undefined, // only replenishable and Update
				wholesaleCorePrice: thePart.wholesaleCorePrice,
				wholesalePrice: thePart.wholesalePrice,
			},
			serials: thePart.serialized
				? thePart.serials.reduce((serials: Array<SerialForSave>, serial) => {
						const newSerial = {
							number: serial.number,
							location: serial.location?.name, // Does the API handle this being absent?
							optionValues: serialOptionValues[serial.id || serial.uuid] || undefined,
						}
						let action: ActionEnum$options = 'CREATE'

						if (serial.deleted) {
							action = 'DELETE'
						} else if (serial.id) {
							action = 'UPDATE'
						}

						if (thePart.innodbInventoryid) {
							// Only need id and action on existing parts
							serials.push({
								...newSerial,
								id: serial.id,
								action,
							})
						} else if (!serial.deleted) {
							// on new parts, don't need to send deleted serials
							serials.push({ ...newSerial, action: 'CREATE' })
						}
						return serials
				  }, [])
				: [],
		}
	}

	function getNewCollectionMemberForSave(member: LoadedCollectionMember): NewCollectionMember {
		return {
			costFactor: member.costFactor,
			integral: member.integral,
			quantity: member.quantity,
			// They wil either have a skuNumber or a new part
			existingSkuNumber: member.part.skuNumber,
			newInventory: member.part.skuNumber
				? undefined
				: getNewPartForSave(member.part, member.attachments, member.optionValueMap),
		}
	}

	function getUpdateCollectionMemberForSave(member: LoadedCollectionMember & { id: number }): CollectionMemberUpdate {
		return {
			id: member.id,
			costFactor: member.costFactor,
			integral: member.integral,
			quantity: member.quantity,
		}
	}

	function getNewPartForSave(
		thePart = part,
		theAttachments = attachments,
		theOptionValueMap = optionValueMap,
	): NewInventory {
		const {
			attachments,
			locations,
			optionValues,
			part: partForSave,
			serials,
		} = getGenericPartForSave(thePart, theAttachments, theOptionValueMap)

		const partForCreate: NewInventory = {
			...partForSave,
			attachments: attachments.create,
			inventoryTypeId: thePart.inventoryTypeId!,
			storeId: thePart.storeId!,
			serials,
			optionValues,
			locations: locations.create,
			collection: thePart.collection
				? {
						type: thePart.collection.type,
						members: thePart.collection.members.reduce((acc: Array<NewCollectionMember>, member) => {
							if (member.loaded) {
								acc.push(getNewCollectionMemberForSave(member))
							}
							return acc
						}, []),
				  }
				: undefined,
		}
		return partForCreate
	}

	function getUpdatePartForSave(
		thePart = part,
		theAttachments = attachments,
		theOptionValueMap = optionValueMap,
	): InventoryUpdate {
		const {
			attachments,
			locations,
			optionValues,
			part: partForSave,
			serials,
		} = getGenericPartForSave(thePart, theAttachments, theOptionValueMap)

		const partForUpdate: InventoryUpdate = {
			...partForSave,
			id: thePart.innodbInventoryid,
			attachments: attachments,
			locations,
			optionValues,
			serials,
			collection: thePart.collection
				? {
						type: thePart.collection.type,
						members: {
							additions: collectionCrudStore.createdValues.reduce((acc: Array<NewCollectionMember>, member) => {
								if (member.loaded) {
									acc.push(getNewCollectionMemberForSave(member))
								}
								return acc
							}, []),
							removals: collectionCrudStore.deletedValues.reduce((acc: Array<number>, member) => {
								if (member.id) {
									acc.push(member.id)
								}
								return acc
							}, []),
							updates: collectionCrudStore.updatedValues.reduce((acc: Array<CollectionMemberUpdate>, member) => {
								if (member.id && member.loaded) {
									acc.push(
										getUpdateCollectionMemberForSave({
											...member,
											id: member.id,
										}),
									)
								}
								return acc
							}, []),
						},
				  }
				: undefined,
		}
		return partForUpdate
	}

	export function logChangeIfNeccessary() {
		if (!part.innodbInventoryid || !!partChangeComment) {
			return true
		}

		partChangesToLog = []

		if (settingValues.inventory.fields.category.logChanges && origPart.category?.id !== part.category?.id) {
			partChangesToLog.unshift({
				property: 'Category',
				previousValue: origPart.category?.name ?? null,
				newValue: part.category?.name ?? null,
			})
		}
		if (settingValues.inventory.fields.condition.logChanges && origPart.condition !== part.condition) {
			partChangesToLog.unshift({
				property: 'Condition',
				previousValue: origPart.condition,
				newValue: part.condition,
			})
		}
		if (settingValues.inventory.fields.coreCost.logChanges && origPart.coreCost !== part.coreCost) {
			partChangesToLog.unshift({
				property: 'Core Cost',
				previousValue: origPart.coreCost,
				newValue: part.coreCost,
			})
		}
		if (settingValues.inventory.fields.cost.logChanges && origPart.cost !== part.cost) {
			partChangesToLog.unshift({
				property: 'Cost',
				previousValue: origPart.cost,
				newValue: part.cost,
			})
		}
		if (
			settingValues.inventory.fields.distributorCorePrice.logChanges &&
			origPart.distributorCorePrice !== part.distributorCorePrice
		) {
			partChangesToLog.unshift({
				property: 'Distributor Core Price',
				previousValue: origPart.distributorCorePrice,
				newValue: part.distributorCorePrice,
			})
		}
		if (
			settingValues.inventory.fields.distributorPrice.logChanges &&
			origPart.distributorPrice !== part.distributorPrice
		) {
			partChangesToLog.unshift({
				property: 'Distributor Price',
				previousValue: origPart.distributorPrice,
				newValue: part.distributorPrice,
			})
		}
		if (settingValues.inventory.fields.glCategory.logChanges && origPart.glCategory?.id !== part.glCategory?.id) {
			partChangesToLog.unshift({
				property: 'GL Category',
				previousValue: origPart.glCategory?.name ?? null,
				newValue: part.glCategory?.name ?? null,
			})
		}
		if (
			settingValues.inventory.fields.inventoryType.logChanges &&
			origPart.inventoryType?.id !== part.inventoryType?.id
		) {
			partChangesToLog.unshift({
				property: 'Inventory Type',
				previousValue: origPart.inventoryType?.id.toString() ?? null,
				newValue: part.inventoryType?.id.toString() ?? null,
			})
		}
		if (
			settingValues.inventory.fields.jobberCorePrice.logChanges &&
			origPart.jobberCorePrice !== part.jobberCorePrice
		) {
			partChangesToLog.unshift({
				property: 'Jobber Core Price',
				previousValue: origPart.jobberCorePrice,
				newValue: part.jobberCorePrice,
			})
		}
		if (settingValues.inventory.fields.jobberPrice.logChanges && origPart.jobberPrice !== part.jobberPrice) {
			partChangesToLog.unshift({
				property: 'Jobber Price',
				previousValue: origPart.jobberPrice,
				newValue: part.jobberPrice,
			})
		}
		if (settingValues.inventory.fields.listPrice.logChanges && origPart.listPrice !== part.listPrice) {
			partChangesToLog.unshift({
				property: 'List Price',
				previousValue: origPart.listPrice,
				newValue: part.listPrice,
			})
		}
		if (settingValues.inventory.fields.oemNumber.logChanges && origPart.oemNumber !== part.oemNumber) {
			partChangesToLog.unshift({
				property: 'OEM Number',
				previousValue: origPart.oemNumber,
				newValue: part.oemNumber,
			})
		}
		if (settingValues.inventory.fields.retailPrice.logChanges && origPart.retailPrice !== part.retailPrice) {
			partChangesToLog.unshift({
				property: 'Retail Price',
				previousValue: origPart.retailPrice,
				newValue: part.retailPrice,
			})
		}
		if (
			settingValues.inventory.fields.retailCorePrice.logChanges &&
			origPart.retailCorePrice !== part.retailCorePrice
		) {
			partChangesToLog.unshift({
				property: 'Retail Core Price',
				previousValue: origPart.retailCorePrice,
				newValue: part.retailCorePrice,
			})
		}
		if (settingValues.inventory.fields.side.logChanges && origPart.side !== part.side) {
			partChangesToLog.unshift({
				property: 'Side',
				previousValue: origPart.side,
				newValue: part.side,
			})
		}
		if (settingValues.inventory.fields.userStatus.logChanges && origPart.userStatus !== part.userStatus) {
			partChangesToLog.unshift({
				property: 'User Status',
				previousValue: origPart.userStatus,
				newValue: part.userStatus,
			})
		}
		if (
			settingValues.inventory.fields.defaultVendor.logChanges &&
			origPart.defaultVendor?.id !== part.defaultVendor?.id
		) {
			partChangesToLog.unshift({
				property: 'Vendor',
				previousValue: origPart.defaultVendor?.code.toString() ?? null,
				newValue: part.defaultVendor?.code.toString() ?? null,
			})
		}
		if (settingValues.inventory.fields.wholesalePrice.logChanges && origPart.wholesalePrice !== part.wholesalePrice) {
			partChangesToLog.unshift({
				property: 'Wholesale Price',
				previousValue: origPart.wholesalePrice,
				newValue: part.wholesalePrice,
			})
		}
		if (
			settingValues.inventory.fields.wholesaleCorePrice.logChanges &&
			origPart.wholesaleCorePrice !== part.wholesaleCorePrice
		) {
			partChangesToLog.unshift({
				property: 'Wholesale Core Price',
				previousValue: origPart.wholesaleCorePrice,
				newValue: part.wholesaleCorePrice,
			})
		}

		if (partChangesToLog.length) {
			showPartChangeModal = true
			tick().then(() => {
				const changeCommentInput = document.getElementById('part-change-comment')
				if (changeCommentInput && changeCommentInput instanceof HTMLTextAreaElement) {
					changeCommentInput.focus()
				}
			})
			return false
		}
		return true
	}

	/** Do all the validation stuff, and then return true if we should proceed to saving */
	async function checkCanSavePart(thePart = part): Promise<boolean> {
		const savingCollectionMember = thePart !== part
		console.log('saving', savingCollectionMember ? 'collection member' : 'parent part')
		if (!hasPermission('PARTS_CAN_SAVE_PARTS', thePart.storeId)) {
			alert('You do not have permission to save parts.')
			return false
		}
		// We'll need to skip some stuff if we're saving collection members
		// TODO: don't skip the important stuff that I'm too lazy to handle
		if (savingCollectionMember) {
			return true
		}

		if (!partChangeComment && !logChangeIfNeccessary()) {
			return false
		}

		if (thePart.quantity > 0 && !thePart.locations.length && !thePart.serialized && !savingCollectionMember) {
			showNoLocationOnSaveModal = true
			return false
		}

		const canSave = await autoRefreshThing.isSafeToSaveEntity()
		// Don't trigger if we're saving a different part
		if (!canSave && !savingCollectionMember) {
			return false
		}

		// Check for duplicate tag number & warn
		if (thePart.tagNumber && !savingCollectionMember) {
			const { data } = await inventoryLookupQuery.fetch({
				variables: {
					filter: {
						tagNumber: thePart.tagNumber,
						statuses: ['A', 'H'],
					},
				},
			})
			const duplicateCount = data?.inventories.items.filter(p => p.skuNumber !== thePart.skuNumber).length ?? 0
			if (
				duplicateCount &&
				!confirm(
					`The part you are attempting to save was detected to have a duplicate Tag # with ${duplicateCount} other part entries.\nIf you continue saving, you could create redundancies in your records.`,
				)
			) {
				return false
			}
		}

		// check for duplicate vendor/partNumberStripped at this store (replenishable only)
		if (thePart.replenishable && thePart.defaultVendor?.id && thePart.partNumberStripped && !savingCollectionMember) {
			const { data } = await inventoryLookupQuery.fetch({
				variables: {
					filter: {
						replenishable: true,
						partNumberStripped: thePart.partNumberStripped || thePart.partNumber,
						isCore: false,
						statuses: ['A', 'H'],
						stores: [thePart.storeId],
						defaultVendorId: thePart.defaultVendor.id,
					},
				},
			})
			const duplicate = thePart.innodbInventoryid
				? data?.inventories.items.find(p => p.skuNumber !== thePart.skuNumber)
				: data?.inventories.items[0]
			if (
				duplicate &&
				!confirm(
					`Warning: At least one part (${duplicate.tagNumber}) has the same purchase vendor and part number as this part.`,
				)
			) {
				return false
			}
		}

		// warn about quantity creation on replenishable parts
		const accountingEnabled = settingValues.accounting.useGlAccounting
		if (
			accountingEnabled &&
			thePart.replenishable &&
			!thePart.innodbInventoryid &&
			!savingCollectionMember &&
			!confirm(
				`You're creating a replenishable part with an initial quantity (not 0).\nInventory created this way has no value (average cost) and will not show up in accounting (a manual adjustment may be required).`,
			)
		) {
			return false
		}

		// Ensure Quantity <= 1 for Single Quantity parts
		if (thePart.singleQuantity && thePart.quantity > 1 && !savingCollectionMember) {
			if (
				confirm(
					`This part is 'Single Quantity' but it has a quantity on hand greater than 1. Do you want to mark this part as not 'Single Quantity'?`,
				)
			) {
				thePart.singleQuantity = false
			} else {
				return false
			}
		}

		// Ensure Single Quantity parts only have one location
		if (
			thePart.singleQuantity &&
			thePart.locations.filter(location => location.quantity).length > 1 &&
			!savingCollectionMember
		) {
			if (
				confirm(
					`This part is 'Single Quantity' but it has multiple locations\n(The part should either be marked as a multi-quantity part, or the locations should be corrected)\n\nDo you want to mark this part as not 'Single Quantity'?`,
				)
			) {
				thePart.singleQuantity = false
			} else {
				return false
			}
		}

		// Check for attachments on deleted parts
		if (
			thePart.status === 'D' &&
			origPart.status !== 'D' &&
			!savingCollectionMember &&
			confirm(
				`This part has been marked as deleted, also clean all ${attachments.length} attachment(s) associated with it?`,
			)
		) {
			attachments = attachments.map(attachment => ({
				...attachment,
				action: 'DELETE',
			}))
		}

		// Check QoH vs # of serials
		if (
			thePart.serialized &&
			thePart.serials.length !== thePart.quantity &&
			!savingCollectionMember &&
			!confirm(
				`Warning: the quantity on hand (${thePart.quantity}) at this store doesn't match the # of valid serial #'s (${thePart.serials.length}).\r\nIf this is not corrected, it could cause issues when selling or consuming the part.`,
			)
		) {
			return false
		}

		if (thePart.vehicleId !== origPart.vehicleId && !savingCollectionMember) {
			// TODO: Fetch real quantities when we have API to allow it
			const quantities = {
				salesOrderLine: 0,
				jobPart: 0,
				purchaseOrderLine: 0,
				transferOrderLine: 0,
			}

			if (Object.values(quantities).some(qty => qty > 0)) {
				alert(
					`Warning: This part's vehicle association has changed, and the item was detected on historical order(s) (${quantities.salesOrderLine} SOs, ${quantities.jobPart} WOs, ${quantities.purchaseOrderLine} POs, ${quantities.transferOrderLine} TOs)\nIn order to avoid cost discrepancies, these historical orders will be updated to reflect this vehicle change.`,
				)
			}
		}

		const varianceLocationName = settingValues?.inventory.varianceLocationName || 'Variance'

		if (
			settingValues?.location.enforceLocationHierarchy &&
			!savingCollectionMember &&
			thePart.locations.some(
				location => !Number.isInteger(location.locationId) && location.name !== varianceLocationName,
			)
		) {
			const virtualLocationName =
				thePart.locations.find(location => !Number.isInteger(location.locationId))?.name ?? 'Unknown'
			alert(
				`You are trying to use a location (${virtualLocationName}) that does not exist.\r\n\r\nMake sure are using locations that exist, or get permission from an administrator to use virtual locations.`,
			)
			return false
		}
		return true
	}

	async function savePart(
		thePart = part,
		theAttachments = attachments,
		theOptionValueMap = optionValueMap,
	): Promise<void> {
		const canSavePart = await checkCanSavePart(thePart)
		if (!canSavePart) {
			return
		}
		const savingCollectionMember = thePart !== part
		try {
			let savedPart: {
				innodbInventoryid: number
				storeId: number
				sku: number
				tagNumber: string
			} | null = null
			if (thePart.innodbInventoryid) {
				const { data } = await updateInventory.mutate({
					part: getUpdatePartForSave(thePart, theAttachments, theOptionValueMap),
					logMessage: partChangeComment || undefined,
				})
				savedPart = data?.part ?? null
			} else {
				const { data } = await createInventory.mutate({
					part: getNewPartForSave(thePart, theAttachments, theOptionValueMap),
				})
				savedPart = data?.part ?? null
			}

			if (!savedPart) {
				throw new Error('No part returned from API. Maybe there was an error?')
			}

			// If we've updated some collection members, run it back for each one of them
			const updatedCollectionMembers = collectionCrudStore.updatedValues
			if (updatedCollectionMembers.length && !savingCollectionMember) {
				for (const member of updatedCollectionMembers) {
					if (member.loaded && member.id) {
						await savePart(member.part, member.attachments, member.optionValueMap)
					}
				}
			}

			// If this is a collection member save, we can skip the rest
			if (savingCollectionMember) {
				return
			}

			// Clear the cached part if we're saving it
			const cachedPart = getObject<{ innodbInventoryid: number }>(localStorage, 'cachedPart')
			if (cachedPart && (loadCachedPart || cachedPart.innodbInventoryid === savedPart.innodbInventoryid)) {
				clearCachedPart()
			}

			let saveMessage = `Part saved successfully.`

			// Show Tag # in save message if it was auto-generated, and if the part has a vehicle or the setting says to
			if (!thePart.tagNumber && (thePart.vehicleId || userSettings.parts.showTagNumberWhenCreating)) {
				saveMessage = `Part saved successfully and assigned Tag # ${savedPart.tagNumber}.`
			}

			mediator.publish('showMessage', {
				type: 'success',
				heading: 'Saved!',
				message: saveMessage,
				time: 10,
			})
			partChanged = false

			asr.go(
				null,
				{
					loadCachedPart: false,
					sku: $clearScreenOnSave ? null : savedPart.sku,
					lastSavedTime: Date.now(),
					tab,
				},
				{ replace: true },
			)
		} catch (err: any) {
			let message: string
			if (Array.isArray(err)) {
				message = err.map(e => e.message).join('\n')
			} else {
				message = err.message
			}
			// I guess $queryStore.fetching doesn't get set to false if there's an error?
			isSaving = false
			return mediator.publish('showMessage', {
				type: 'danger',
				heading: 'Failed to save part',
				message,
				time: false,
			})
		}
	}
	//#endregion Saving

	export function canLeaveState() {
		if (partChanged) {
			return confirm(
				'You have unsaved changes. Are you sure you want to leave?\n\nYour changes have been saved for later, click "Unsaved Part" in "Recent Activity" to resume your work.',
			)
		}
		return true
	}

	onMount(() => {
		if (part.inventoryId && !loadCachedPart) {
			mediator.publish('activity', {
				title: `#${part.tagNumber || part.inventoryId} (${inventoryTypeData?.inventoryTypeId})`,
				type: 'PART',
				route: 'app.part',
				name: 'Part',
				parameters: {
					sku: part.skuNumber ? part.skuNumber.toString() : 'null',
					storeId: part.storeId ? part.storeId.toString() : 'null',
				},
				icon: 'engine',
			})
		}
		// only need to to check for changes if we have an existing part
		if (part.inventoryId) {
			autoRefreshThing.startTimer()
		}
		// Tick so everything that modifies/binds the part on init/mount doesn't trigger change detection 10 times
		tick().then(() => {
			skipChangeDetection = false
		})
	})

	// #region Qureries

	const getDateModified = graphql(`
		query GetPartDateModified($innodbInventoryid: UInt!) {
			inventory(id: $innodbInventoryid) {
				dateModified
			}
		}
	`)

	const replicateInventory = graphql(`
		mutation LIB_replicateInventory($sourceInventoryId: Int!, $destinationStoreIds: [Int!]!) {
			replicateInventory(sourceInventoryId: $sourceInventoryId, destinationStoreIds: $destinationStoreIds) {
				result
				storeId: destinationStoreId
				inventory {
					id
					store {
						id
						code
						name
					}
				}
			}
		}
	`)

	const createInventory = graphql(`
		mutation LIB_createInventory($part: NewInventory!) {
			part: createInventory(input: $part) {
				innodbInventoryid: id
				storeId
				sku
				tagNumber
			}
		}
	`)

	const updateInventory = graphql(`
		mutation LIB_updateInventoryBasic($part: InventoryUpdate!, $logMessage: String) {
			part: updateInventory(input: $part, logMessage: $logMessage) {
				innodbInventoryid: id
				storeId
				sku
				tagNumber
			}
		}
	`)

	const inventoryLookupQuery = graphql(`
		query SaveInventoryLookup($filter: InventoryFilter) {
			inventories(filter: $filter) {
				items {
					id
					store {
						id
						code
						name
					}
					tagNumber
					skuNumber
					inventoryType {
						id
						name
					}
				}
			}
		}
	`)

	// #endregion
</script>

<div class="card">
	<div class="card-header">
		<div class="d-flex flex-wrap justify-content-between">
			<div class="mr-auto">
				<h4 class="inline">
					<i class="fas fa-engine"></i>
					<span class="d-inline-block">
						{partTitle.title}
						{#if partTitle.subtitle}
							<small>{partTitle.subtitle}</small>{/if}
					</span>
				</h4>
			</div>
			<div
				class="d-flex flex-wrap d-sm-block mb-2 mb-sm-0"
				style="gap: 5px"
			>
				{#if lastInventorySearch.current}
					<Button
						icon="history"
						size="sm"
						outline
						href={asr.makePath('app.part-search.results', lastInventorySearch.current)}
					>
						Back to Search
					</Button>
				{/if}
				<Dropdown
					split
					size="sm"
					class="flex-grow-1"
					iconClass="plus"
					color="success"
					outline
					disabled={!hasPermission('PARTS_CAN_SAVE_PARTS', part.storeId) ||
						!hasPermission('PARTS_CAN_EDIT_INFO', part.storeId)}
					href={asr.makePath(null, { storeId, lastResetTime: Date.now() })}
				>
					New Part
					{#snippet dropdownItems()}
						<DropdownItem
							icon="clone"
							onclick={() => clonePart()}
						>
							Clone Part
							<br />
							<small>Copy this part's information to a new part</small>
						</DropdownItem>
						<DropdownItem
							icon="copy"
							onclick={() => replicatePart()}
						>
							Replicate Part
							<br />
							<small>Replicate this part to other stores</small>
						</DropdownItem>
					{/snippet}
				</Dropdown>
				<Dropdown
					size="sm"
					outline
					iconClass="shop"
					parentDivClass="d-inline"
					title="Click to open this part at other stores"
					disabled={part.skuType.inventories.length < 2}
				>
					{part.skuType.inventories.find(sku => sku.store.id === part.storeId)?.store.code ?? ''}
					{#snippet dropdownItems()}
						<h6 class="dropdown-header">View Part at Other Store</h6>
						{#each part.skuType.inventories as { store }}
							{@const show = hasPermission('PARTS_CAN_VIEW_PARTS', store.id)}
							<a
								class="dropdown-item"
								class:active={store.id === part.storeId}
								class:disabled={!show}
								href={show
									? asr.makePath(null, { sku: part.skuNumber, storeId: store.id, loadCachedPart: false })
									: null}
							>
								{store.code} - {store.name}
							</a>
						{/each}
					{/snippet}
				</Dropdown>
				<Button
					outline
					size="sm"
					icon="print"
					title="Print Tag"
					onclick={() => {
						reportJobModal.open({
							type: 'Tag',
							name: 'Tag',
							parameters: {
								subselect: `(SELECT ${part.inventoryId} AS inventoryid, ${part.storeId} AS storeid, NULL AS inventoryserialid, ${part.locations[0].name} AS location, ${part.quantity} AS quantity) as \`tuples\``,
							},
						})
					}}
				></Button>
				<Button
					outline
					size="sm"
					iconClass="magnifying-glass"
					title="Click to search for parts by Tag #"
					disabled={!hasPermission('PARTS_CAN_VIEW_PARTS', part.storeId)}
					onclick={() => loadPartModal.show()}
				></Button>
				<Dropdown
					split
					menuItemClickCloses={false}
					size="sm"
					class="flex-grow-1"
					iconClass="floppy-disk"
					isLoading={isSaving}
					color={requiredFieldsComplete ? 'primary' : 'danger'}
					disabled={!requiredFieldsComplete || !partChanged || !hasPermission('PARTS_CAN_SAVE_PARTS', part.storeId)}
					colorGreyDisabled={requiredFieldsComplete}
					onclick={() => {
						savePart()
					}}
					>Save Part{#if !requiredFieldsComplete}*{/if}
					{#snippet dropdownItems()}
						<h6 class="dropdown-header">Save and...</h6>
						<DropdownCheckboxItem bind:checked={$clearScreenOnSave}>Start New Part (Clear Screen)</DropdownCheckboxItem>
					{/snippet}
				</Dropdown>
			</div>
		</div>
		<NavTabBar
			{tabs}
			bind:selectedTab={tab}
			tabChange={tab => asr.go(null, { tab }, { inherit: true })}
		>
			{#snippet append()}
				{#if !requiredFieldsComplete}
					<span class="text-danger mb-2 mr-2 ml-auto mt-auto"
						>Missing required fields: {missingRequiredFieldsList}
					</span>
				{/if}
			{/snippet}
		</NavTabBar>
	</div>
	<div class="card-body pb-0">
		{#if tab == 'basic'}
			<PartBasic
				{asr}
				{assyModels}
				{attachments}
				{glCategories}
				{inventoryConditions}
				{inventoryTypeList}
				{partStoreLocations}
				{saleClasses}
				{sellPriceClasses}
				{settingValues}
				{userPartStatusList}
				{vehicleMakes}
				{vehicleModels}
				{vendorList}
				{shouldDisable}
				bind:part
				bind:origPart
				bind:typeFields
				bind:optionValueMap
				bind:inventoryTypeData
				partModels={trueModels}
				qaChange={cacheOptionValueMap}
			></PartBasic>
		{:else if tab == 'attachments'}
			<Attachments
				bind:fileList={attachments}
				modificationDisabled={!hasPermission('PARTS_CAN_EDIT_ATTACHMENTS', part.storeId)}
				filesAdded={() => cacheAttachments()}
				filesDeleted={() => cacheAttachments()}
				publicChange={() => cacheAttachments()}
				rankChange={() => cacheAttachments()}
			></Attachments>
		{:else if tab == 'history'}
			<History {part}></History>
		{:else if tab === 'links'}
			<Links
				{asr}
				{assyModels}
				{glCategories}
				{inventoryConditions}
				{inventoryTypeList}
				{partStoreLocations}
				{saleClasses}
				{sellPriceClasses}
				{settingValues}
				{userPartStatusList}
				{vehicleMakes}
				{vehicleModels}
				{vendorList}
				{shouldDisable}
				partModels={trueModels}
				{userSettings}
				bind:part
			></Links>
		{/if}
	</div>
</div>

<Modal
	bind:show={showNoLocationOnSaveModal}
	title="No Location Selected"
	confirmShown={false}
	cancelButtonText="Cancel"
	close={() => (showNoLocationOnSaveModal = false)}
>
	<p>This item has {part.quantity} quantity on hand, but no locations.</p>
	<p class="p-0">Either add the default or variance location, or cancel and add a different location.</p>
	{#snippet footer()}
		<Button
			disabled={!settingValues?.inventory.defaultLocationName}
			onclick={() => addLocationAndSave(settingValues?.inventory.defaultLocationName)}>Use Default</Button
		>
		<Button
			disabled={!settingValues?.inventory.varianceLocationName}
			onclick={() => addLocationAndSave(settingValues?.inventory.varianceLocationName ?? 'Variance')}
			>Use Variance</Button
		>
	{/snippet}
</Modal>

<Modal
	bind:show={showPartChangeModal}
	title={partChangesToLog.length === 1 ? `${partChangesToLog[0].property} Has Changed` : `Multiple Fields Have Changed`}
	subtitle="Please comment on the reason for the change before saving."
	cancelButtonText="Cancel"
	confirmButtonText="Save"
	confirmButtonFormId="part-change-comment-form"
	confirm={() => {
		console.log('saving part post change thingy')
		showPartChangeModal = false
		savePart()
	}}
>
	<ul class="list-group">
		{#each partChangesToLog as change}
			<li class="list-group-item">
				{change.property} changed from "{change.previousValue}" to "{change.newValue}"
			</li>
		{/each}
	</ul>
	<form
		id="part-change-comment-form"
		onsubmit={event => event.preventDefault()}
	>
		<TextArea
			id="part-change-comment"
			label="Comment"
			bind:value={partChangeComment}
		></TextArea>
	</form>
</Modal>

<PartSearchModal
	chooseItem={item => asr.go(null, { sku: item.skuNumber, storeId: item.store.id })}
	bind:this={loadPartModal}
></PartSearchModal>

<AutoRefreshThing
	hasLocalChanges={() => partChanged}
	getRemoteDateModified={async () => {
		if (!part.innodbInventoryid) {
			return null
		}

		const { data } = await getDateModified.fetch({
			variables: {
				innodbInventoryid: part.innodbInventoryid,
			},
		})

		return data?.inventory?.dateModified ?? null
	}}
	refresh={() => {
		partChanged = false
		asr.go(null, { lastResetTime: Date.now() }, { replace: true, inherit: true })
	}}
	localDateModified={part.dateModified}
	ignoreRefreshEvent={showStoreModal || showNoLocationOnSaveModal}
	fullObjectName="Part"
	autoRefreshObjectTitle="Part"
	verb="Save"
	bind:this={autoRefreshThing}
></AutoRefreshThing>

<StoreSelectModal
	bind:show={showStoreModal}
	subtitle="Select Stores to Replicate Part to"
	excludedStoreIds={partStoreIds}
	on:confirm={event => confirmReplicatePart(event.detail.selectedStoreIds)}
	bind:this={storeSelectModal}
></StoreSelectModal>

<ReportJobModal bind:this={reportJobModal} />

<svelte:window bind:scrollY />

{#if scrollY > 100}
	<div
		style="position: fixed; bottom: 10px; right: 10px; z-index: 10000;"
		transition:fade={{ duration: 200 }}
	>
		<Button
			tabindex={-1}
			color="secondary"
			iconClass="arrow-up"
			title="Scroll to top"
			onclick={() =>
				scrollTo({
					top: 0,
					left: 0,
					behavior: 'smooth',
				})}
		></Button>
	</div>
{/if}
